import { ConfirmationModal, CustomButton, CustomChip, SkeletonLoading } from '@components';
import {
  Typography,
  Divider,
  Avatar,
  Box,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { useCallback, useContext, useMemo, useState } from 'react';
import { appContext, capitalizeFirstLetter, delay, formatMoney } from '@utils';
import ArrowBack from '@mui/icons-material/ArrowBackIos';
import MoreVert from '@mui/icons-material/MoreVert';
import {
  BillingModel,
  OrganizationBilling,
  OrganizationStatus,
  SubscriptionStatus,
} from '@graphql';
import { COLORS } from '@styles';
// import AddCard from '@mui/icons-material/AddCard';
import CreditCard from '@mui/icons-material/CreditCard';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import Percent from '@mui/icons-material/Percent';
import DeleteOutlined from '@assets/svg/DeleteOutlined.svg';
import ElektrafiLogo from '@assets/svg/ElektrafiLogo.svg';
import BorderColorOutlined from '@mui/icons-material/BorderColorOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { RequestStatus } from '@store';
import { toast } from 'react-toastify';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { PaymentMethodAvatarStyle, styles } from './styles';
import { formatDate } from '../../../utils';
import { ApplyCreditDrawer } from '../ApplyCreditDrawer';
import { ApplyDiscountCodeDrawer } from '../ApplyDiscountCodeDrawer';
import { ManagePlansAndSeats } from '../../ManagePlansAndSeats/ManagePlansAndSeats';
import { EditPricingDrawer } from '../EditPricingDrawer/EditPricingDrawer';

interface CancelSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  organizationName: string;
  date: string;
  error: boolean;
  message?: string;
}

interface ResumeSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  organizationName: string;
  error: boolean;
  message?: string;
}

interface HideSectionProps {
  hide: boolean;
  children: React.ReactNode;
}

interface PaymentMethodAvatarProps {
  card?: {
    brand?: string;
    last4?: string;
    expiry?: string;
  };
  organizationName?: string;
  billingModel?: BillingModel;
  hasPaymentMethod?: boolean;
}

interface SubscriptionActionButtonProps {
  organizationBillingStatus: SubscriptionStatus;
  openCancelSubscription: () => void;
  openRestoreSubscription: () => void;
  handleCancelDowngradeSubscription: () => void;
  disableActionButton: boolean;
  billingModel: BillingModel;
}

function CancelSubscriptionModal({
  open,
  onClose,
  onConfirm,
  organizationName,
  date,
  error,
  message,
}: CancelSubscriptionModalProps) {
  const formattedDate = formatDate(date);
  return (
    <ConfirmationModal
      icon={<img src={DeleteOutlined} alt="delete-outlined" />}
      title={`Cancel ${organizationName} subscription?`}
      onClose={onClose}
      isOpen={open}
      variant="destructive"
      confirmationText={organizationName || ''}
      onConfirm={onConfirm}
      confirmationButtonLabel="Cancel Subscription"
      hasError={error}
      message={message}
    >
      <p>
        Canceling their subscription will remove ElektraFi access for all team members and stop
        future billing on <strong>{formattedDate}</strong>, at the end of their current billing
        period.
      </p>
      <p>Type the organization name to confirm cancellation: </p>
      <strong>{organizationName}</strong>
    </ConfirmationModal>
  );
}

function ResumeSubscriptionModal({
  open,
  onClose,
  onConfirm,
  organizationName,
  error,
  message,
}: ResumeSubscriptionModalProps) {
  const formattedDate = formatDate(dayjs().toISOString());

  return (
    <ConfirmationModal
      icon={<img src={ElektrafiLogo} alt="delete-outlined" />}
      title={`Resume ${organizationName} subscription?`}
      onClose={onClose}
      isOpen={open}
      variant="default"
      onConfirm={onConfirm}
      confirmationButtonLabel="Resume Subscription"
      confirmationText={organizationName || ''}
      hasError={error}
      message={message}
    >
      <p>
        This will immediately charge <strong>{organizationName}</strong> and restore ElektraFi
        access for all team members.The company subscription renewal will restart from{' '}
        <strong>{`today's date ${formattedDate}`}</strong>, meaning that their next billing cycle
        will begin now rather than continuing from the previous schedule.
      </p>
      <p>Type the organization name to confirm: </p>
      <strong>{organizationName}</strong>
    </ConfirmationModal>
  );
}

function HideSectionWrapper({ hide, children }: HideSectionProps): React.ReactElement | null {
  if (hide) {
    return null;
  }

  return children as React.ReactElement;
}

function hasSubscription(organizationBilling: OrganizationBilling | null): boolean {
  if (!organizationBilling) {
    return false;
  }

  return [
    SubscriptionStatus.Active,
    SubscriptionStatus.Trialing,
    SubscriptionStatus.CancelingAtPeriodEnd,
    SubscriptionStatus.Canceled,
    SubscriptionStatus.Downgrading,
  ].includes(organizationBilling?.status as SubscriptionStatus);
}

function SubscriptionStatusChip({
  organizationBilling,
}: {
  organizationBilling: OrganizationBilling;
}) {
  const status = organizationBilling?.status;

  const trialEndsAtDate = dayjs(organizationBilling?.trialEndsAt).format('MMM DD');

  switch (status) {
    case SubscriptionStatus.Pending:
      return <CustomChip label="Pending" variant="dark" />;
    case SubscriptionStatus.Trialing:
      return <CustomChip label={`Trial ends ${trialEndsAtDate}`} variant="default" />;
    case SubscriptionStatus.Active:
    case SubscriptionStatus.Downgrading:
      return <CustomChip label="Active" variant="success" />;
    case SubscriptionStatus.CancelingAtPeriodEnd:
      return <CustomChip label="Ending" variant="warning" />;
    case SubscriptionStatus.PastDue:
      return <CustomChip label="Past Due" variant="error" />;
    case SubscriptionStatus.Canceled:
      return <CustomChip label="Inactive" variant="dark" />;
    default:
      return <CustomChip label="Pending" variant="dark" />;
  }
}

function SubscriptionActionButton({
  organizationBillingStatus,
  openCancelSubscription,
  openRestoreSubscription,
  handleCancelDowngradeSubscription,
  disableActionButton,
  billingModel,
}: SubscriptionActionButtonProps): React.ReactElement | null {
  if (!organizationBillingStatus) {
    return null;
  }

  if (organizationBillingStatus === SubscriptionStatus.Pending || billingModel === BillingModel.INVOICE) {
    return null;
  }

  if (organizationBillingStatus === SubscriptionStatus.Downgrading) {
    return (
      <CustomButton
        variant="default"
        width="fit-content"
        onClick={handleCancelDowngradeSubscription}
        disabled={disableActionButton}
      >
        Cancel Downgrade
      </CustomButton>
    );
  }

  if (
    organizationBillingStatus === SubscriptionStatus.CancelingAtPeriodEnd ||
    organizationBillingStatus === SubscriptionStatus.Canceled
  ) {
    return (
      <CustomButton
        variant="default"
        width="fit-content"
        onClick={openRestoreSubscription}
        disabled={disableActionButton}
      >
        Resume Subscription
      </CustomButton>
    );
  }

  return (
    <CustomButton
      variant="destructive"
      width="fit-content"
      onClick={openCancelSubscription}
      disabled={disableActionButton}
    >
      Cancel Subscription
    </CustomButton>
  );
}

const TopSection = observer(() => {
  const {
    organizationStore: { organizationInContext: organization, refreshOrganizationViews },
    billingStore: {
      invoices,
      organizationBilling,
      cancelSubscription,
      restoreSubscription,
      cancelSubscriptionData,
      restoreSubscriptionData,
      reactivateSubscriptionData,
      getOrganizationBilling,
      reactivateSubscription,
      getInvoices,
      cancelDowngradeOrganizationPlan,
      organizationBillingData: { status: organizationBillingStatus },
    },
    historyStore: { history },
  } = useContext(appContext);

  const [isOpenCancelSubscriptionModal, setIsOpenCancelSubscriptionModal] = useState(false);
  const [isOpenRestoreSubscriptionModal, setIsOpenRestoreSubscriptionModal] = useState(false);

  const openCancelSubscription = () => setIsOpenCancelSubscriptionModal(true);
  const openRestoreSubscription = () => setIsOpenRestoreSubscriptionModal(true);

  const closeCancelSubscription = () => setIsOpenCancelSubscriptionModal(false);
  const closeRestoreSubscription = () => setIsOpenRestoreSubscriptionModal(false);

  const refetchData = useCallback(() => {
    getOrganizationBilling(organization?.id as string);
    refreshOrganizationViews(organization?.id);
  }, [getOrganizationBilling, organization?.id, refreshOrganizationViews]);

  const handleCancelSubscription = async () => {
    const response = await cancelSubscription(organization?.id as string);

    if (response) {
      // Delay to wait for the webhook to be processed
      await delay(2000);
      toast(`${organization?.name} subscription cancelled successfully`, { type: 'success' });
      closeCancelSubscription();
      refetchData();
    }
  };

  const handleResumeSubscription = async () => {
    let response = null;

    if (
      ![SubscriptionStatus.CancelingAtPeriodEnd, SubscriptionStatus.Canceled].includes(
        organizationBilling?.status as SubscriptionStatus,
      )
    ) {
      return;
    }

    if (organizationBilling?.status === SubscriptionStatus.CancelingAtPeriodEnd) {
      response = await restoreSubscription(organization?.id as string);
    } else {
      response = await reactivateSubscription(organization?.id as string);
    }

    // Delay to wait for the webhook to be processed
    await delay(2000);

    if (response?.success) {
      toast(`${organization?.name} subscription reactivated successfully`, { type: 'success' });
      closeRestoreSubscription();
      refetchData();
    } else {
      toast('Something went wrong!', { type: 'error' });
      getInvoices(organization?.id as string);
    }
  };

  const handleCancelDowngradeSubscription = async () => {
    const response = await cancelDowngradeOrganizationPlan(organization?.id as string);

    // Delay to wait for the webhook to be processed
    await delay(2000);

    if (response?.success) {
      toast(`${organization?.name} subscription downgraded successfully`, { type: 'success' });
      refetchData();
    }
  };

  const totalRevenue = invoices?.reduce((acc, invoice) => acc + invoice.amountPaid, 0) ?? 0;

  const { organizationName, organizationLogo, status } = useMemo(() => {
    if (!organization) {
      return {
        organizationName: '',
        organizationLogo: '',
        status: OrganizationStatus.Created,
      };
    }

    return {
      organizationName: organization?.name,
      organizationLogo: organization?.logoUrl,
      status: organization?.status,
    };
  }, [organization]);

  const disableActionButton = useMemo(
    () =>
      [SubscriptionStatus.Pending, SubscriptionStatus.PastDue].includes(
        organizationBilling?.status as SubscriptionStatus,
      ),
    [organizationBilling?.status],
  );

  const { errorStatus, errorMessage } = useMemo(() => {
    if (reactivateSubscriptionData.status === RequestStatus.FAILED) {
      return {
        errorStatus: true,
        errorMessage: reactivateSubscriptionData.error as string,
      };
    }

    if (restoreSubscriptionData.status === RequestStatus.FAILED) {
      return {
        errorStatus: true,
        errorMessage: restoreSubscriptionData.error as string,
      };
    }

    return {
      errorStatus: false,
      errorMessage: '',
    };
  }, [
    reactivateSubscriptionData.status,
    reactivateSubscriptionData.error,
    restoreSubscriptionData.status,
    restoreSubscriptionData.error,
  ]);

  const isSubscriptionActive = useMemo(
    () => hasSubscription(organizationBilling),
    [organizationBilling],
  );

  const isLoading = organizationBillingStatus === RequestStatus.LOADING;

  return (
    <>
      <SkeletonLoading isLoading={isLoading}>
        <div style={styles.topSectionContainer}>
          <Box sx={styles.backButtonContainer} onClick={() => history.goBack()}>
            <ArrowBack />
            <Typography variant="body1">Back</Typography>
          </Box>
          <Divider style={styles.divider} flexItem orientation="vertical" variant="middle" />
          <div style={styles.organizationInfoSectionContainer}>
            <div style={styles.organizationNameContainer}>
              <Avatar src={organizationLogo ?? ''} alt={organizationName}>
                {organizationName?.charAt(0)}
              </Avatar>
              {organizationName}
            </div>
            <SubscriptionStatusChip
              organizationBilling={organizationBilling as OrganizationBilling}
            />
          </div>
          <Divider style={styles.divider} flexItem orientation="vertical" variant="middle" />
          <div style={styles.revenueSectionContainer}>
            <div style={styles.revenueItemsContainer}>
              <div style={styles.revenueItemContainer}>
                <Typography variant="body1" style={styles.revenueItemTitle}>
                  Total Revenue
                </Typography>
                <Typography variant="h6" style={styles.revenueItemValue}>
                  {formatMoney(totalRevenue)}
                </Typography>
              </div>
              <HideSectionWrapper hide>
                <div style={styles.revenueItemContainer}>
                  <Typography variant="body1" style={styles.revenueItemTitle}>
                    Monthly Recurring Revenue
                  </Typography>
                  <Typography variant="h6" style={styles.monthlyRevenueItemValue}>
                    {formatMoney(0)}
                  </Typography>
                </div>
              </HideSectionWrapper>
            </div>
            <HideSectionWrapper hide={status === OrganizationStatus.Created || !isSubscriptionActive}>
              <SubscriptionActionButton
                organizationBillingStatus={organizationBilling?.status as SubscriptionStatus}
                openCancelSubscription={openCancelSubscription}
                openRestoreSubscription={openRestoreSubscription}
                handleCancelDowngradeSubscription={handleCancelDowngradeSubscription}
                disableActionButton={disableActionButton}
                billingModel={organizationBilling?.billingModel as BillingModel}
              />
            </HideSectionWrapper>
          </div>
        </div>
      </SkeletonLoading>
      <CancelSubscriptionModal
        open={isOpenCancelSubscriptionModal}
        onClose={closeCancelSubscription}
        onConfirm={handleCancelSubscription}
        organizationName={organizationName}
        date={organizationBilling?.nextBillingDate as string}
        error={cancelSubscriptionData.status === RequestStatus.FAILED}
        message={cancelSubscriptionData.error as string}
      />
      <ResumeSubscriptionModal
        open={isOpenRestoreSubscriptionModal}
        onClose={closeRestoreSubscription}
        onConfirm={handleResumeSubscription}
        organizationName={organizationName}
        error={errorStatus}
        message={errorMessage}
      />
    </>
  );
});

function PaymentMethodAvatar(props: PaymentMethodAvatarProps) {
  const { card, organizationName, billingModel, hasPaymentMethod } = props;

  const paymentMethod = useMemo(() => {
    if (billingModel === BillingModel.INVOICE) {
      return {
        heading: 'Invoice',
        description: organizationName,
        icon: <ArticleOutlined fontSize='small' />,
      };
    }

    if (!hasPaymentMethod) {
      return {
        heading: 'Setup Required',
        description: 'This organization has not set up a payment method yet.',
        icon: <ErrorOutlineIcon fontSize='small' />,
      };
    }

    return {
      heading: `●●●● ${card?.last4}`,
      description: `${card?.brand} • Expiry ${card?.expiry}`,
      icon: <CreditCard fontSize='small' />,
    };
  }, [card, organizationName, billingModel, hasPaymentMethod]);

  const paymentMethodStyle = useMemo(() => {
    if (!hasPaymentMethod && billingModel !== BillingModel.INVOICE) {
      return PaymentMethodAvatarStyle.WARNING;
    }

    return PaymentMethodAvatarStyle.DEFAULT;
  }, [hasPaymentMethod, billingModel]);

  return (
    <div style={styles.creditCardContainer}>
      <div style={{
        ...styles.creditCardIconContainer,
        ...styles.creditCardIconColor[paymentMethodStyle],
      }}>
        {paymentMethod.icon}
      </div>
      <div style={styles.creditCardItem}>
        <Typography variant="body1" fontSize="0.875rem" style={styles.creditCardHeader[paymentMethodStyle]}>
          {paymentMethod.heading}
        </Typography>
        <Typography variant="body1" fontSize="0.75rem" style={styles.creditCardDescription[paymentMethodStyle]}>
          {paymentMethod.description}
        </Typography>
      </div>
    </div>
  );
}

const BillingDetails = observer(() => {
  const {
    organizationStore: { organizationInContext: organization },
    billingStore: { organizationBilling, organizationBillingData: { status: organizationBillingStatus } },
  } = useContext(appContext);

  const { currentTier } = organization;
  const [isOpenApplyCreditDrawer, setIsOpenApplyCreditDrawer] = useState(false);
  const [isOpenManagePlansAndSeatsDrawer, setIsOpenManagePlansAndSeatsDrawer] = useState(false);
  const [isOpenApplyDiscountDrawer, setIsOpenApplyDiscountDrawer] = useState(false);
  const [isOpenEditPricingDrawer, setIsOpenEditPricingDrawer] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // const handleOpenApplyCreditDrawer = () => {
  //   setIsOpenApplyCreditDrawer(true);
  //   handleClose();
  // }
  const handleCloseApplyCreditDrawer = () => setIsOpenApplyCreditDrawer(false);

  const handleOpenApplyDiscountDrawer = () => {
    setIsOpenApplyDiscountDrawer(true);
    handleClose();
  };
  const handleCloseApplyDiscountDrawer = () => setIsOpenApplyDiscountDrawer(false);

  const handleOpenManagePlansAndSeatsDrawer = () => {
    setIsOpenManagePlansAndSeatsDrawer(true);
  };

  const handleCloseManagePlansAndSeatsDrawer = () => setIsOpenManagePlansAndSeatsDrawer(false);
  const handleOpenEditPricingDrawer = () => setIsOpenEditPricingDrawer(true);
  const handleCloseEditPricingDrawer = () => setIsOpenEditPricingDrawer(false);

  const mockCreditBalance = 1110;

  const {
    createdAtDate,
    seatsUsage,
    costPerPeriod,
    totalSeats,
    creditCardLast4,
    creditCardBrand,
    creditCardExpiry,
    hasPaymentMethod,
    accountStatusLabel,
    trialEndsAt,
    billingModel,
  } = useMemo(() => {
    const endAtDate = formatDate(organizationBilling?.endsAt || '');
    const orgCreatedAtDate = formatDate(organization?.createdAt || '');
    const nextRenewalDate = formatDate(organizationBilling?.nextBillingDate as string || '');


    let accountStatusLabelText = '';

    if (organizationBilling?.billingModel === BillingModel.INVOICE && organizationBilling?.endsAt) {
      const endDate = dayjs(organizationBilling?.endsAt).add(1, "day").toISOString()
      accountStatusLabelText = `Next renewal ${formatDate(endDate || "")}`;
    } else if (organizationBilling?.status === SubscriptionStatus.CancelingAtPeriodEnd) {
      accountStatusLabelText = `Subscription will end at ${endAtDate}`;
    } else if (organizationBilling?.status === SubscriptionStatus.Canceled) {
      accountStatusLabelText = `Cancelled in ${endAtDate}`;
    } else if (organizationBilling?.status === SubscriptionStatus.Downgrading) {
      accountStatusLabelText = `Downgrading in ${nextRenewalDate}`;
    } else {
      accountStatusLabelText = `Next renewal ${nextRenewalDate}`;
    }

    return {
      createdAtDate: orgCreatedAtDate,
      accountStatusLabel: accountStatusLabelText,
      seatsUsage: organizationBilling?.usedSeats || 0,
      costPerPeriod: organizationBilling?.subscriptionDetails?.amount,
      totalSeats: organizationBilling?.purchasedSeats || 0,
      creditCardLast4: organizationBilling?.paymentMethod?.last4,
      creditCardBrand: capitalizeFirstLetter(organizationBilling?.paymentMethod?.brand ?? ''),
      creditCardExpiry: `${String(organizationBilling?.paymentMethod?.expMonth).padStart(2, '0')}/${organizationBilling?.paymentMethod?.expYear
        }`,
      hasPaymentMethod: Boolean(organizationBilling?.paymentMethod),
      trialEndsAt: organizationBilling?.trialEndsAt,
      billingModel: organizationBilling?.billingModel,
    };
  }, [organizationBilling, organization?.createdAt]);

  const isLoading = organizationBillingStatus === RequestStatus.LOADING;

  return (
    <>
      <SkeletonLoading isLoading={isLoading}>
        <div style={styles.billingDetailsContainer}>
          <div style={styles.billingContentContainer}>
            <div style={styles.billingContentHeaderWrapper}>
              <div style={styles.billingContentHeaderItem}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  fontSize="1.25rem"
                >{`${currentTier?.name} Plan`}</Typography>
                <Typography variant="body1" fontSize="0.75rem">{`Member since ${formatDate(
                  createdAtDate,
                )}`}</Typography>
              </div>
              <div style={styles.planDetailsWrapper}>
                {organizationBilling?.status === SubscriptionStatus.Trialing && trialEndsAt && (
                  <div style={styles.trialItem}>
                    <Typography variant="h6" fontWeight={600} fontSize="0.875rem">
                      Trial Active
                    </Typography>
                    <Typography variant="body1" fontSize="0.75rem">{`Expires in ${formatDate(
                      trialEndsAt,
                    )}`}</Typography>
                  </div>
                )}
                <Box
                  sx={styles.paymentMethodHeaderAction}
                  onClick={handleOpenManagePlansAndSeatsDrawer}
                >
                  <BorderColorOutlined />
                </Box>
                <HideSectionWrapper hide>
                  <div style={styles.planDetailsItem}>
                    <Typography variant="body1" fontSize="0.75rem">
                      Credit Balance
                    </Typography>
                    <Typography variant="body1" fontWeight={600} fontSize="1.25rem">
                      {formatMoney(mockCreditBalance, 2)}
                    </Typography>
                  </div>
                </HideSectionWrapper>
              </div>
            </div>
            <div style={styles.seatsUsageContainer}>
              <Typography
                variant="body1"
                fontSize="0.875rem"
                fontWeight={600}
              >{`${seatsUsage} of ${totalSeats} seats used`}</Typography>
              <LinearProgress
                variant="determinate"
                value={((seatsUsage ?? 0) / (totalSeats ?? 1)) * 100}
              />
            </div>
            <Divider style={styles.billingDivider} flexItem />
            <div style={styles.seatCostContainer}>
              <div style={styles.seatCostItem}>
                <Typography variant="h6" fontSize="1.5rem" fontWeight={600}>
                  {formatMoney(costPerPeriod ?? 0)}
                </Typography>
                <Typography variant="body1" fontSize="0.875rem" color={COLORS.NEUTRAL_LIGHT_0}>
                  /period
                </Typography>
              </div>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Typography
                  variant="body1"
                  fontSize="0.875rem"
                >{`for ${totalSeats} seats`}</Typography>
                {organizationBilling?.billingModel === BillingModel.INVOICE && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        color: COLORS.NEUTRAL_LIGHT_0,
                      }}
                      onClick={handleOpenEditPricingDrawer}
                    >
                      <EditOutlined fontSize="small" />
                      <Typography variant="body2">Edit Pricing</Typography>
                    </Box>
                  </>
                )}
              </Box>
            </div>
          </div>
          <Divider style={styles.billingDivider} flexItem orientation="vertical" variant="middle" />
          <div style={styles.billingContentContainer}>
            <div style={styles.paymentMethodHeaderWrapper}>
              <div style={styles.paymentMethodHeaderItem}>
                <Typography variant="h6" fontSize="1.5rem" fontWeight={600}>
                  Payment Method
                </Typography>
                <Typography variant="body1" fontSize="0.875rem">
                  Check how this organization will be charged for their ElektraFi plan.
                </Typography>
              </div>
              {/* This should be removed if we add more options  to the menu */}
              {billingModel !== BillingModel.INVOICE && (
                <Box sx={styles.paymentMethodHeaderAction} onClick={handleOpen}>
                  <MoreVert />
                </Box>
              )}
              <Menu
                id="payment-method-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={styles.creditCardMenu}
              >
                {billingModel !== BillingModel.INVOICE && (
                  <MenuItem onClick={handleOpenApplyDiscountDrawer}>
                    <ListItemIcon>
                      <Percent />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography variant="body1" fontSize="0.875rem">
                        Apply Discount Code
                      </Typography>
                    </ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </div>
            <PaymentMethodAvatar
              card={{
                last4: creditCardLast4,
                brand: creditCardBrand,
                expiry: creditCardExpiry,
              }}
              organizationName={organization?.name}
              billingModel={billingModel}
              hasPaymentMethod={hasPaymentMethod}
            />
            <div style={styles.nextRenewalContainer}>
              <Divider style={styles.billingDivider} flexItem />
              <Typography variant="body1" fontSize="0.75rem">
                {accountStatusLabel}
              </Typography>
            </div>
          </div>
        </div>
      </SkeletonLoading>
      <ApplyCreditDrawer
        open={isOpenApplyCreditDrawer}
        onClose={handleCloseApplyCreditDrawer}
        organization={organization}
      />
      <ApplyDiscountCodeDrawer
        open={isOpenApplyDiscountDrawer}
        onClose={handleCloseApplyDiscountDrawer}
        organization={organization}
      />
      <ManagePlansAndSeats
        open={isOpenManagePlansAndSeatsDrawer}
        onClose={handleCloseManagePlansAndSeatsDrawer}
        organization={organization}
      />
      <EditPricingDrawer
        open={isOpenEditPricingDrawer}
        onClose={handleCloseEditPricingDrawer}
        organization={organization}
      />
    </>
  );
});

export const OrganizationBillingHeader = observer(() => (
  <>
    <TopSection />
    <BillingDetails />
    </>
));
